import type { Props as SearchbarProps } from "~/components/search/Searchbar";
import Drawers from "~/components/Header/Drawers";

import type { ImageWidget } from "~/types/widgets";
import Alert, { AlertBarProps } from "~/components/Header/Alert";
import Navbar, { B2BButton } from "~/components/Header/Navbar";
import HeaderJS from "~/components/Header/HeaderJS";
import HeaderHeightSync from "~/components/Header/HeaderHeightSync";
import type { Brand, MenuNavigationItem } from "./types";
import WrapperHeader from "~/components/Header/WrapperHeader";

export interface Props {
  /**
   * @title Alerts
   * @description Topbar alerts
   */
  alerts: AlertBarProps;

  /**
   * @title Brands
   * @description Brand logos shown in the desktop topbar and the mobile menu drawer
   */
  brands: Brand[];

  /** @title Search Bar */
  searchbar?: SearchbarProps;

  /**
   * @title Navigation items
   * @description Menu navigation items
   */
  navItems?: MenuNavigationItem[] | null;

  /**
   * @title Menu rules bar text
   * @description Text to appear on each navigation item rules bar
   */
  rulesBarText?: string;

  /** @title Logo */
  logo?: {
    lightSrc?: ImageWidget;
    darkSrc?: ImageWidget;
    alt?: string;
    logoWidth?: number;
    logoHeight?: number;
    /**
     * @title Logo link
     * @description Página aberta ao clicar no logo. Use a home da marca deste header (ex: /vivame). Padrão: /
     */
    url?: string;
  };

  /**
   * @title B2B Button
   */
  b2bButton?: B2BButton;
  /**
   * @title Free Shipping Target (Minicart bar)
   * @description Target for free shipping on minicart bar. If empty, the bar does not appear.
   */
  freeShippingTarget?: number;
  /**
   * @title Transluscent effect
   * @description Translucent effect on header for hover and scroll
   */
  translucentEffect?: boolean;
}

function Header({
  alerts,
  brands,
  searchbar,
  navItems,
  logo,
  rulesBarText,
  b2bButton,
  freeShippingTarget,
  translucentEffect = false,
}: Props) {
  const items = navItems ?? [];

  const logoUrl = logo?.url || "/";

  const lightLogo = {
    src: logo?.lightSrc ?? "",
    alt: logo?.alt ?? "",
    logoWidth: logo?.logoWidth,
    logoHeight: logo?.logoHeight,
    url: logoUrl,
  };
  const darkLogo = {
    src: logo?.darkSrc ?? "",
    alt: logo?.alt ?? "",
    logoWidth: logo?.logoWidth,
    logoHeight: logo?.logoHeight,
    url: logoUrl,
  };

  return (
    <>
      <header>
        <WrapperHeader translucentEffect={translucentEffect}>
          <Drawers
            menu={{ items }}
            brands={brands}
            searchbar={searchbar}
            freeShippingTarget={freeShippingTarget}
          >
            <div
              className={`header fixed w-full z-50 ${
                !translucentEffect ? "scrolled" : ""
              }`}
            >
              <Alert {...alerts} brands={brands} />

              <Navbar
                items={items}
                rulesBarText={rulesBarText}
                searchbar={searchbar && { ...searchbar }}
                logo={translucentEffect ? lightLogo : darkLogo}
                b2bButton={b2bButton}
                translucentEffect={translucentEffect}
              />
            </div>
          </Drawers>
          <div
            className={`header-background ${
              translucentEffect ? "block lg:hidden" : "block"
            }`}
          />
          <HeaderHeightSync />
          {translucentEffect && (
            <HeaderJS
              lightLogoImage={lightLogo?.src as string}
              darkLogoImage={darkLogo?.src as string}
            />
          )}
        </WrapperHeader>
      </header>
    </>
  );
}

export default Header;
